import React, { useEffect, useState, useMemo } from "react";
import { Pagination, DropdownButton, Dropdown, Button } from 'react-bootstrap';
import './LocalDeviceConfigurations.css';
import { extractDeviceConfigurations } from '../utils/firebaseDataExtractor';
import { fetchData } from '../Firebase/FirebaseManager';
import ExcelJS from 'exceljs';

function LocalDeviceConfigurations() {
    const [configData, setConfigData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [loading, setLoading] = useState(true);
    const [environment, setEnvironment] = useState('PROD');

    // Load and process Firebase data when environment changes
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                
                // Fetch data from Firebase using selected environment
                const firebaseData = await fetchData(environment);
                
                if (!firebaseData) {
                    console.error('No data received from Firebase');
                    setConfigData([]);
                    return;
                }
                
                // Extract configurations from Firebase data
                const extractedData = extractDeviceConfigurations(firebaseData);
                
                // Sort by latest date/time first
                // Compare Vehicle Pairing and Install Config dates, use whichever is more recent
                const sortedData = extractedData.sort((a, b) => {
                    const getLatestDate = (record) => {
                        const vehiclePairingDate = record.vehiclePairingDateTime !== 'N/A' ? new Date(record.vehiclePairingDateTime) : null;
                        const installConfigDate = record.installConfigDateTime !== 'N/A' ? new Date(record.installConfigDateTime) : null;
                        
                        if (!vehiclePairingDate && !installConfigDate) return null;
                        if (!vehiclePairingDate) return installConfigDate;
                        if (!installConfigDate) return vehiclePairingDate;
                        
                        return vehiclePairingDate > installConfigDate ? vehiclePairingDate : installConfigDate;
                    };
                    
                    const dateA = getLatestDate(a);
                    const dateB = getLatestDate(b);
                    
                    if (!dateA && !dateB) return 0;
                    if (!dateA) return 1;
                    if (!dateB) return -1;
                    
                    return dateB - dateA; // Most recent first
                });
                
                setConfigData(sortedData);
            } catch (error) {
                console.error('Error processing Firebase data:', error);
                setConfigData([]);
            } finally {
                setLoading(false);
            }
        };
        
        loadData();
    }, [environment]);

    const handleEnvironmentChange = (env) => {
        setEnvironment(env);
        setCurrentPage(1);
    };

    const columns = useMemo(
        () => [
            { Header: "VIN", accessor: "vin", width: "150px" },
            { Header: (<>Device Serial<br/><small>Number</small></>), accessor: "deviceSerialNumber", width: "160px" },
            { Header: (<>Firmware<br/><small>Version</small></>), accessor: "firmwareVersion", width: "140px" },
            { Header: (<>Recommended<br/><small>Vehicle Interface Spec</small></>), accessor: "recommendedVehicleInterfaceSpec", width: "280px" },
            { Header: (<>Current<br/><small>Vehicle Interface Spec</small></>), accessor: "currentVehicleInterfaceSpec", width: "260px" },
            { Header: "Site ID", accessor: "siteId", width: "130px" },
            { Header: (<>Vehicle Pairing<br/><small>Date & Time</small></>), accessor: "vehiclePairingDateTime", width: "200px" },
            { Header: (<>Install Config<br/><small>Date & Time</small></>), accessor: "installConfigDateTime", width: "200px" },
            { Header: (<>Key<br/><small>(Hour Meter Config)</small></>), accessor: "keySensor", width: "220px" },
            { Header: (<>Lift<br/><small>(Hour Meter Config)</small></>), accessor: "liftSensor", width: "220px" },
            { Header: (<>Operator Presence<br/><small>(Hour Meter Config)</small></>), accessor: "operatorPresenceSensor", width: "280px" },
            { Header: (<>Travel<br/><small>(Hour Meter Config)</small></>), accessor: "travelSensor", width: "230px" },
            { Header: "Manuf. Year", accessor: "manufYear", width: "120px" },
            { Header: "Vehicle Type", accessor: "vehicleType", width: "130px" },
            { Header: "Vehicle Model", accessor: "vehicleModel", width: "130px" },
            { Header: "Vehicle Serial", accessor: "vehicleSerial", width: "130px" },
            { Header: (<>Mobile App<br/><small>Version</small></>), accessor: "mobileAppVersion", width: "150px" },
            { Header: (<>Mobile<br/><small>Brand</small></>), accessor: "mobileBrand", width: "130px" },
            { Header: "Mobile Model", accessor: "mobileModel", width: "130px" },
            { Header: "OS", accessor: "os", width: "100px" },
            { Header: (<>User<br/><small>Lang Pref</small></>), accessor: "userLanguagePref", width: "160px" },
            { Header: "User ID", accessor: "userId", width: "180px" },
            { Header: "User Name", accessor: "userName", width: "160px" },
            { Header: "User Role", accessor: "userRole", width: "140px" }
        ],
        []
    );

    // Calculate pagination
    const totalPages = Math.ceil(configData.length / pageSize);
    const indexOfLastItem = currentPage * pageSize;
    const indexOfFirstItem = indexOfLastItem - pageSize;
    const currentData = configData.slice(indexOfFirstItem, indexOfLastItem);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (size) => {
        setPageSize(size);
        setCurrentPage(1);
    };

    const formatCellValue = (value) => {
        if (value === null || value === undefined || value === '') return 'N/A';
        return value;
    };

    const exportToExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Local Device Configurations');

        // Define columns with proper headers and widths
        worksheet.columns = [
            { header: 'VIN', key: 'vin', width: 20 },
            { header: 'Device Serial Number', key: 'deviceSerialNumber', width: 20 },
            { header: 'Firmware Version', key: 'firmwareVersion', width: 18 },
            { header: 'Recommended Vehicle Interface Spec', key: 'recommendedVehicleInterfaceSpec', width: 35 },
            { header: 'Current Vehicle Interface Spec', key: 'currentVehicleInterfaceSpec', width: 35 },
            { header: 'Site ID', key: 'siteId', width: 15 },
            { header: 'Vehicle Pairing Date & Time', key: 'vehiclePairingDateTime', width: 25 },
            { header: 'Install Config Date & Time', key: 'installConfigDateTime', width: 25 },
            { header: 'Key (Hour Meter Config)', key: 'keySensor', width: 25 },
            { header: 'Lift (Hour Meter Config)', key: 'liftSensor', width: 25 },
            { header: 'Operator Presence (Hour Meter Config)', key: 'operatorPresenceSensor', width: 35 },
            { header: 'Travel (Hour Meter Config)', key: 'travelSensor', width: 25 },
            { header: 'Manufacture Year', key: 'manufYear', width: 18 },
            { header: 'Vehicle Type', key: 'vehicleType', width: 20 },
            { header: 'Vehicle Model', key: 'vehicleModel', width: 18 },
            { header: 'Vehicle Serial', key: 'vehicleSerial', width: 18 },
            { header: 'Mobile App Version', key: 'mobileAppVersion', width: 20 },
            { header: 'Mobile Brand', key: 'mobileBrand', width: 15 },
            { header: 'Mobile Model', key: 'mobileModel', width: 18 },
            { header: 'OS', key: 'os', width: 12 },
            { header: 'User Language Preference', key: 'userLanguagePref', width: 25 },
            { header: 'User ID', key: 'userId', width: 30 },
            { header: 'User Name', key: 'userName', width: 20 },
            { header: 'User Role', key: 'userRole', width: 18 }
        ];

    
        worksheet.getRow(1).font = { bold: true, size: 11 };
        worksheet.getRow(1).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF4472C4' }
        };
        worksheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
        worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        worksheet.getRow(1).height = 40;

        configData.forEach((row) => {
            worksheet.addRow({
                vin: formatCellValue(row.vin),
                deviceSerialNumber: formatCellValue(row.deviceSerialNumber),
                firmwareVersion: formatCellValue(row.firmwareVersion),
                recommendedVehicleInterfaceSpec: formatCellValue(row.recommendedVehicleInterfaceSpec),
                currentVehicleInterfaceSpec: formatCellValue(row.currentVehicleInterfaceSpec),
                siteId: formatCellValue(row.siteId),
                vehiclePairingDateTime: formatCellValue(row.vehiclePairingDateTime),
                installConfigDateTime: formatCellValue(row.installConfigDateTime),
                keySensor: formatCellValue(row.keySensor),
                liftSensor: formatCellValue(row.liftSensor),
                operatorPresenceSensor: formatCellValue(row.operatorPresenceSensor),
                travelSensor: formatCellValue(row.travelSensor),
                manufYear: formatCellValue(row.manufYear),
                vehicleType: formatCellValue(row.vehicleType),
                vehicleModel: formatCellValue(row.vehicleModel),
                vehicleSerial: formatCellValue(row.vehicleSerial),
                mobileAppVersion: formatCellValue(row.mobileAppVersion),
                mobileBrand: formatCellValue(row.mobileBrand),
                mobileModel: formatCellValue(row.mobileModel),
                os: formatCellValue(row.os),
                userLanguagePref: formatCellValue(row.userLanguagePref),
                userId: formatCellValue(row.userId),
                userName: formatCellValue(row.userName),
                userRole: formatCellValue(row.userRole)
            });
        });

        // Apply borders and alignment to all cells
        worksheet.eachRow((row, rowNumber) => {
            row.eachCell((cell) => {
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };
                if (rowNumber > 1) {
                    cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
                }
            });
        });

        // Generate Excel file
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Local_Device_Configurations_${new Date().toISOString().split('T')[0]}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
    };

    return (
        <div className="LocalDeviceConfigurations">
            <div className="LocalDeviceConfigurations-header">
                <div className="BodyLocalDeviceConfigurations">
                    Local Device Configurations
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <DropdownButton
                        id="environment-dropdown"
                        title={environment}
                        variant="outline-primary"
                        onSelect={(e) => handleEnvironmentChange(e)}
                    >
                        <Dropdown.Item eventKey="QA">QA</Dropdown.Item>
                        <Dropdown.Item eventKey="UAT">UAT</Dropdown.Item>
                        <Dropdown.Item eventKey="PROD">PROD</Dropdown.Item>
                    </DropdownButton>
                    <Button 
                        style={{ backgroundColor: '#007bff', borderColor: '#007bff' }}
                        onClick={exportToExcel}
                        disabled={loading || configData.length === 0}
                    >
                       Export
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="ldc-loading" style={{ textAlign: 'center', padding: '40px' }}>
                    <p>Loading device configurations...</p>
                </div>
            ) : configData.length === 0 ? (
                <div className="ldc-no-data" style={{ textAlign: 'center', padding: '40px' }}>
                    <p>No device configurations found.</p>
                </div>
            ) : (
                <>
                    <div className="ldc-table-container">
                        <div className="ldc-table-wrapper">
                            <table className="ldc-table">
                                <thead>
                                    <tr>
                                        {columns.map((column, index) => (
                                            <th key={index} style={{ minWidth: column.width }}>
                                                {column.Header}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentData.map((row, rowIndex) => (
                                        <tr key={row.id}>
                                            {columns.map((column, colIndex) => (
                                                <td key={colIndex}>
                                                    {formatCellValue(row[column.accessor])}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {totalPages > 1 && (
                            <div className="ldc-pagination-container">
                                <div className="ldc-pagination-left">
                                    <span className="pagination-label">Page Size:</span>
                                    <DropdownButton
                                        id="page-size-dropdown"
                                        title={`${pageSize}`}
                                        variant="outline-dark"
                                        size="sm"
                                        onSelect={(e) => handlePageSizeChange(Number(e))}
                                    >
                                        <Dropdown.Item eventKey="10">10</Dropdown.Item>
                                        <Dropdown.Item eventKey="20">20</Dropdown.Item>
                                        <Dropdown.Item eventKey="30">30</Dropdown.Item>
                                        <Dropdown.Item eventKey="50">50</Dropdown.Item>
                                    </DropdownButton>
                                </div>

                                <div className="ldc-pagination-center">
                                    <Pagination className="mb-0">
                                        <Pagination.First 
                                            onClick={() => handlePageChange(1)} 
                                            disabled={currentPage === 1} 
                                        />
                                        <Pagination.Prev 
                                            onClick={() => handlePageChange(currentPage - 1)} 
                                            disabled={currentPage === 1} 
                                        />
                                        <span className="pagination-info">
                                            Page <b className="text-primary">{currentPage}</b> of <b className="text-primary">{totalPages}</b>
                                        </span>
                                        <span className="pagination-info">
                                            <b>Total Records:</b> <b className="text-primary">{configData.length}</b>
                                        </span>
                                        <Pagination.Next 
                                            onClick={() => handlePageChange(currentPage + 1)} 
                                            disabled={currentPage === totalPages} 
                                        />
                                        <Pagination.Last 
                                            onClick={() => handlePageChange(totalPages)} 
                                            disabled={currentPage === totalPages} 
                                        />
                                    </Pagination>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

export default LocalDeviceConfigurations;
